import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import i18n from "@/i18n";
import { applyStockMovement, getCurrentClinicId } from "@/hooks/useStockMovements";
import { getStockEntryQuantity } from "@/lib/xml-import/stock-entry";
import { getCurrentClinicCustomerId } from "@/lib/utils/clinic-context";
import {
  XML_IMPORT_STORAGE_BUCKET,
  buildXmlImportDownloadName,
  buildXmlImportStoragePath,
} from "@/lib/fiscal/xml-import-storage";

export interface XmlImportItem {
  numeroItem?: string;
  codigo: string;
  descricao: string;
  ncm: string;
  cfop: string;
  cest?: string;
  origem?: string;
  icmsSituacaoTributaria?: string;
  pisSituacaoTributaria?: string;
  cofinsSituacaoTributaria?: string;
  unidade: string;
  quantidade: number;
  valorUnitario: number;
  valorTotal: number;
  ean?: string;
  gtinTributavel?: string;
  unidadeTributavel?: string;
  quantidadeTributavel?: number;
  valorUnitarioTributavel?: number;
  valorDesconto?: number;
  valorFrete?: number;
  valorSeguro?: number;
  valorOutrasDespesas?: number;
  valorTotalTributos?: number;
  icmsModalidadeBaseCalculo?: string;
  icmsBaseCalculo?: number;
  icmsAliquota?: number;
  icmsValor?: number;
  ipiSituacaoTributaria?: string;
  ipiEnquadramento?: string;
  ipiBaseCalculo?: number;
  ipiAliquota?: number;
  ipiValor?: number;
  pisBaseCalculo?: number;
  pisAliquota?: number;
  pisValor?: number;
  cofinsBaseCalculo?: number;
  cofinsAliquota?: number;
  cofinsValor?: number;
  ibsCbsSituacaoTributaria?: string;
  ibsCbsClassificacaoTributaria?: string;
  ibsCbsBaseCalculo?: number;
  cbsAliquota?: number;
  cbsValor?: number;
  ibsUfAliquota?: number;
  ibsUfValor?: number;
  ibsMunAliquota?: number;
  ibsMunValor?: number;
  ibsValorTotal?: number;
  isValor?: number;
}

export interface ParsedXmlData {
  supplier: {
    cnpj: string;
    name: string;
    fantasia: string;
    endereco: {
      logradouro: string;
      numero: string;
      bairro: string;
      municipio: string;
      uf: string;
      cep: string;
    };
    phone: string;
    email: string;
  };
  invoice: {
    number: string;
    series: string;
    date: string;
    key: string;
  };
  items: XmlImportItem[];
  totals: {
    subtotal: number;
    discount: number;
    total: number;
  };
}

export interface XmlImport {
  id: string;
  customer_id: string;
  xml_content: string;
  xml_file_path: string | null;
  supplier_cnpj: string | null;
  supplier_name: string | null;
  invoice_number: string | null;
  invoice_key: string | null;
  invoice_issued_at?: string | null;
  imported_at: string;
  imported_by: string | null;
  created_at: string;
  updated_at: string;
  line_count?: number;
}

export interface XmlImportItemSelection {
  xmlItem: XmlImportItem;
  action: "create" | "update" | "skip";
  existingItemId?: string;
  stockQuantity?: number;
  mappedData: {
    name: string;
    sku?: string;
    barcode?: string;
    cost_price: number;
    sale_price: number;
    unit: string;
    product_subtype_id?: string | null;
    ncm?: string;
    cfop?: string;
    cest?: string;
    gtin?: string;
    unidade_tributavel?: string;
    valor_total_tributos?: number;
    aliquota_aproximada_tributos?: number;
    fonte_valor_total_tributos?: string;
    origem?: string;
    icms_tax_situation?: string;
    icms_base_calculo?: number;
    icms_aliquota?: number;
    icms_valor?: number;
    icms_modalidade_base_calculo?: string;
    ipi_tax_situation?: string;
    ipi_enquadramento?: string;
    ipi_base_calculo?: number;
    ipi_aliquota?: number;
    ipi_valor?: number;
    pis_tax_situation?: string;
    pis_base_calculo?: number;
    pis_aliquota?: number;
    pis_valor?: number;
    cofins_tax_situation?: string;
    cofins_base_calculo?: number;
    cofins_aliquota?: number;
    cofins_valor?: number;
    ibs_cbs_situacao_tributaria?: string;
    ibs_cbs_classificacao_tributaria?: string;
    ibs_cbs_base_calculo?: number;
    cbs_aliquota?: number;
    cbs_valor?: number;
    ibs_uf_aliquota?: number;
    ibs_uf_valor?: number;
    ibs_mun_aliquota?: number;
    ibs_mun_valor?: number;
    ibs_valor_total?: number;
    is_valor?: number;
  };
}

const normalizeGtin = (value: string | undefined | null) => {
  const trimmed = value?.trim();
  if (!trimmed || trimmed.toUpperCase() === "SEM GTIN") return null;
  return trimmed;
};

const calculateApproximateTributeRate = (totalTributes?: number, totalValue?: number) => {
  if (typeof totalTributes !== "number" || typeof totalValue !== "number" || totalValue <= 0) return null;
  return Number(((totalTributes / totalValue) * 100).toFixed(4));
};

const buildFiscalPayload = (
  selection: XmlImportItemSelection,
  itemId: string,
  customerId: string,
  rawBarcode: string | null
) => ({
  item_id: itemId,
  customer_id: customerId,
  gtin: normalizeGtin(selection.mappedData.gtin) || rawBarcode || normalizeGtin(selection.xmlItem.gtinTributavel),
  ncm: selection.mappedData.ncm || selection.xmlItem.ncm || null,
  cest: selection.mappedData.cest || selection.xmlItem.cest || null,
  cfop: selection.mappedData.cfop || selection.xmlItem.cfop || null,
  origin_code: selection.mappedData.origem || selection.xmlItem.origem || null,
  unidade_tributavel: selection.mappedData.unidade_tributavel || selection.xmlItem.unidadeTributavel || selection.xmlItem.unidade || null,
  valor_total_tributos: selection.mappedData.valor_total_tributos ?? selection.xmlItem.valorTotalTributos ?? null,
  aliquota_aproximada_tributos:
    selection.mappedData.aliquota_aproximada_tributos ??
    calculateApproximateTributeRate(selection.xmlItem.valorTotalTributos, selection.xmlItem.valorTotal),
  fonte_valor_total_tributos:
    selection.mappedData.fonte_valor_total_tributos ||
    (selection.xmlItem.valorTotalTributos != null ? "imported_xml" : null),
  icms_tax_situation: selection.mappedData.icms_tax_situation || selection.xmlItem.icmsSituacaoTributaria || null,
  icms_base_calculo: selection.mappedData.icms_base_calculo ?? selection.xmlItem.icmsBaseCalculo ?? null,
  icms_aliquota: selection.mappedData.icms_aliquota ?? selection.xmlItem.icmsAliquota ?? null,
  icms_valor: selection.mappedData.icms_valor ?? selection.xmlItem.icmsValor ?? null,
  icms_modalidade_base_calculo:
    selection.mappedData.icms_modalidade_base_calculo || selection.xmlItem.icmsModalidadeBaseCalculo || null,
  ipi_tax_situation: selection.mappedData.ipi_tax_situation || selection.xmlItem.ipiSituacaoTributaria || null,
  ipi_enquadramento: selection.mappedData.ipi_enquadramento || selection.xmlItem.ipiEnquadramento || null,
  ipi_base_calculo: selection.mappedData.ipi_base_calculo ?? selection.xmlItem.ipiBaseCalculo ?? null,
  ipi_aliquota: selection.mappedData.ipi_aliquota ?? selection.xmlItem.ipiAliquota ?? null,
  ipi_valor: selection.mappedData.ipi_valor ?? selection.xmlItem.ipiValor ?? null,
  pis_tax_situation: selection.mappedData.pis_tax_situation || selection.xmlItem.pisSituacaoTributaria || null,
  pis_base_calculo: selection.mappedData.pis_base_calculo ?? selection.xmlItem.pisBaseCalculo ?? null,
  pis_aliquota: selection.mappedData.pis_aliquota ?? selection.xmlItem.pisAliquota ?? null,
  pis_valor: selection.mappedData.pis_valor ?? selection.xmlItem.pisValor ?? null,
  cofins_tax_situation: selection.mappedData.cofins_tax_situation || selection.xmlItem.cofinsSituacaoTributaria || null,
  cofins_base_calculo: selection.mappedData.cofins_base_calculo ?? selection.xmlItem.cofinsBaseCalculo ?? null,
  cofins_aliquota: selection.mappedData.cofins_aliquota ?? selection.xmlItem.cofinsAliquota ?? null,
  cofins_valor: selection.mappedData.cofins_valor ?? selection.xmlItem.cofinsValor ?? null,
  ibs_cbs_situacao_tributaria:
    selection.mappedData.ibs_cbs_situacao_tributaria || selection.xmlItem.ibsCbsSituacaoTributaria || null,
  ibs_cbs_classificacao_tributaria:
    selection.mappedData.ibs_cbs_classificacao_tributaria || selection.xmlItem.ibsCbsClassificacaoTributaria || null,
  ibs_cbs_base_calculo: selection.mappedData.ibs_cbs_base_calculo ?? selection.xmlItem.ibsCbsBaseCalculo ?? null,
  cbs_aliquota: selection.mappedData.cbs_aliquota ?? selection.xmlItem.cbsAliquota ?? null,
  cbs_valor: selection.mappedData.cbs_valor ?? selection.xmlItem.cbsValor ?? null,
  ibs_uf_aliquota: selection.mappedData.ibs_uf_aliquota ?? selection.xmlItem.ibsUfAliquota ?? null,
  ibs_uf_valor: selection.mappedData.ibs_uf_valor ?? selection.xmlItem.ibsUfValor ?? null,
  ibs_mun_aliquota: selection.mappedData.ibs_mun_aliquota ?? selection.xmlItem.ibsMunAliquota ?? null,
  ibs_mun_valor: selection.mappedData.ibs_mun_valor ?? selection.xmlItem.ibsMunValor ?? null,
  ibs_valor_total: selection.mappedData.ibs_valor_total ?? selection.xmlItem.ibsValorTotal ?? null,
  is_valor: selection.mappedData.is_valor ?? selection.xmlItem.isValor ?? null,
});

const buildXmlImportLinePayload = (
  selection: XmlImportItemSelection,
  xmlImportId: string,
  itemId: string,
  action: "create" | "update"
) => ({
  xml_import_id: xmlImportId,
  item_id: itemId,
  line_code: selection.xmlItem.codigo,
  description: selection.xmlItem.descricao,
  ncm: selection.xmlItem.ncm,
  cfop: selection.xmlItem.cfop,
  cest: selection.xmlItem.cest ?? null,
  origin_code: selection.xmlItem.origem ?? null,
  gtin: selection.xmlItem.ean ?? null,
  gtin_tributavel: selection.xmlItem.gtinTributavel ?? null,
  unit: selection.xmlItem.unidade,
  unidade_tributavel: selection.xmlItem.unidadeTributavel ?? null,
  quantity: selection.xmlItem.quantidade,
  quantidade_tributavel: selection.xmlItem.quantidadeTributavel ?? null,
  unit_value: selection.xmlItem.valorUnitario,
  valor_unitario_tributavel: selection.xmlItem.valorUnitarioTributavel ?? null,
  total_value: selection.xmlItem.valorTotal,
  valor_desconto: selection.xmlItem.valorDesconto ?? null,
  valor_frete: selection.xmlItem.valorFrete ?? null,
  valor_seguro: selection.xmlItem.valorSeguro ?? null,
  valor_outras_despesas: selection.xmlItem.valorOutrasDespesas ?? null,
  valor_total_tributos: selection.xmlItem.valorTotalTributos ?? null,
  icms_tax_situation: selection.xmlItem.icmsSituacaoTributaria ?? null,
  icms_modalidade_base_calculo: selection.xmlItem.icmsModalidadeBaseCalculo ?? null,
  icms_base_calculo: selection.xmlItem.icmsBaseCalculo ?? null,
  icms_aliquota: selection.xmlItem.icmsAliquota ?? null,
  icms_valor: selection.xmlItem.icmsValor ?? null,
  ipi_tax_situation: selection.xmlItem.ipiSituacaoTributaria ?? null,
  ipi_enquadramento: selection.xmlItem.ipiEnquadramento ?? null,
  ipi_base_calculo: selection.xmlItem.ipiBaseCalculo ?? null,
  ipi_aliquota: selection.xmlItem.ipiAliquota ?? null,
  ipi_valor: selection.xmlItem.ipiValor ?? null,
  pis_tax_situation: selection.xmlItem.pisSituacaoTributaria ?? null,
  pis_base_calculo: selection.xmlItem.pisBaseCalculo ?? null,
  pis_aliquota: selection.xmlItem.pisAliquota ?? null,
  pis_valor: selection.xmlItem.pisValor ?? null,
  cofins_tax_situation: selection.xmlItem.cofinsSituacaoTributaria ?? null,
  cofins_base_calculo: selection.xmlItem.cofinsBaseCalculo ?? null,
  cofins_aliquota: selection.xmlItem.cofinsAliquota ?? null,
  cofins_valor: selection.xmlItem.cofinsValor ?? null,
  ibs_cbs_situacao_tributaria: selection.xmlItem.ibsCbsSituacaoTributaria ?? null,
  ibs_cbs_classificacao_tributaria: selection.xmlItem.ibsCbsClassificacaoTributaria ?? null,
  ibs_cbs_base_calculo: selection.xmlItem.ibsCbsBaseCalculo ?? null,
  cbs_aliquota: selection.xmlItem.cbsAliquota ?? null,
  cbs_valor: selection.xmlItem.cbsValor ?? null,
  ibs_uf_aliquota: selection.xmlItem.ibsUfAliquota ?? null,
  ibs_uf_valor: selection.xmlItem.ibsUfValor ?? null,
  ibs_mun_aliquota: selection.xmlItem.ibsMunAliquota ?? null,
  ibs_mun_valor: selection.xmlItem.ibsMunValor ?? null,
  ibs_valor_total: selection.xmlItem.ibsValorTotal ?? null,
  is_valor: selection.xmlItem.isValor ?? null,
  action,
});

/**
 * Hook to parse XML file and extract items
 */
export function useParseXml() {
  return useMutation({
    mutationFn: async (xmlContent: string): Promise<ParsedXmlData> => {
      const { data, error } = await supabase.functions.invoke("parse-nfe-xml", {
        body: { xml: xmlContent },
      });

      if (error) throw error;
      return data as ParsedXmlData;
    },
    onError: (error: Error) => {
      toast.error(i18n.t("erp:xmlImportDialog.toast.parseError", { message: error.message }));
    },
  });
}

/**
 * Hook to import selected items from XML
 */
export function useImportXmlItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      xmlContent,
      parsedData,
      selectedItems,
      options,
    }: {
      xmlContent: string;
      parsedData: ParsedXmlData;
      selectedItems: XmlImportItemSelection[];
      options: { includeFiscal: boolean; includeStock: boolean };
    }) => {
      const customerId = await getCurrentClinicCustomerId();
      // Stock is per clinic: resolve it once so every entry lands in the same clinic.
      const stockClinicId = options.includeStock ? await getCurrentClinicId() : undefined;
      const { data: { user } } = await supabase.auth.getUser();
      const xmlFilePath = buildXmlImportStoragePath({
        customerId,
        supplierName: parsedData.supplier.name,
        invoiceDate: parsedData.invoice.date,
        invoiceKey: parsedData.invoice.key,
      });

      const { error: uploadError } = await supabase.storage
        .from(XML_IMPORT_STORAGE_BUCKET)
        .upload(xmlFilePath, new Blob([xmlContent], { type: "application/xml" }), {
          contentType: "application/xml",
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // Create XML import record
      const { data: xmlImport, error: xmlError } = await supabase
        .from("item_xml_imports")
        .insert({
          customer_id: customerId,
          xml_content: xmlContent,
          xml_file_path: xmlFilePath,
          supplier_cnpj: parsedData.supplier.cnpj,
          supplier_name: parsedData.supplier.name,
          invoice_number: parsedData.invoice.number,
          invoice_key: parsedData.invoice.key,
          invoice_issued_at: parsedData.invoice.date || null,
          imported_by: user?.id,
        })
        .select()
        .single();

      if (xmlError) {
        await supabase.storage.from(XML_IMPORT_STORAGE_BUCKET).remove([xmlFilePath]);
        throw xmlError;
      }

      // Process selected items
      const results = [];
      for (const selection of selectedItems) {
        if (selection.action === "skip") continue;

        const rawSku = selection.mappedData.sku?.trim();
        const rawBarcode = normalizeGtin(selection.mappedData.barcode || selection.xmlItem.ean);
        const itemData = {
          type: "PRODUTO" as const,
          name: selection.mappedData.name,
          sku: rawSku ? rawSku : null,
          barcode: rawBarcode,
          cost_price: selection.mappedData.cost_price,
          sale_price: selection.mappedData.sale_price,
          unit: selection.mappedData.unit,
          product_subtype_id: selection.mappedData.product_subtype_id ?? null,
          xml_import_id: xmlImport.id,
          customer_id: customerId,
        };

        const enterStock = async (itemId: string): Promise<string | undefined> => {
          if (!options.includeStock) return undefined;
          const quantity = getStockEntryQuantity(selection.stockQuantity);
          if (quantity <= 0) return undefined;
          try {
            await applyStockMovement({
              item_id: itemId,
              clinic_id: stockClinicId,
              type: "ENTRADA",
              quantity,
              reason: i18n.t("erp:xmlImportDialog.stockReason", { invoice: parsedData.invoice.number }),
            });
            return undefined;
          } catch (stockError) {
            return stockError instanceof Error ? stockError.message : String(stockError);
          }
        };

        if (selection.action === "update" && selection.existingItemId) {
          const { data, error } = await supabase
            .from("items")
            .update(itemData)
            .eq("id", selection.existingItemId)
            .is("deleted_at", null)
            .select()
            .single();

          if (error) {
            console.error(`Error updating item ${selection.existingItemId}:`, error);
            results.push({ success: false, item: selection.mappedData.name, error: error.message });
          } else {
            if (options.includeFiscal) {
              const fiscalPayload = buildFiscalPayload(selection, data.id, customerId, rawBarcode);

              const { error: fiscalError } = await supabase
                .from("item_fiscal_profiles" as any)
                .upsert(fiscalPayload, { onConflict: "item_id" });

              if (fiscalError) {
                throw fiscalError;
              }
            }

            const { error: lineError } = await supabase
              .from("item_xml_import_lines" as any)
              .insert(buildXmlImportLinePayload(selection, xmlImport.id, data.id, "update"));

            if (lineError) throw lineError;

            if (options.includeFiscal) {
              queryClient.invalidateQueries({ queryKey: ["item-fiscal-profile", data.id] });
            }
            results.push({ success: true, item: data, action: "updated", stockError: await enterStock(data.id) });
          }
        } else {
          const { data, error } = await supabase
            .from("items")
            .insert(itemData)
            .select()
            .single();

          if (error) {
            console.error(`Error creating item ${selection.mappedData.name}:`, error);
            results.push({ success: false, item: selection.mappedData.name, error: error.message });
          } else {
            if (options.includeFiscal) {
              const fiscalPayload = buildFiscalPayload(selection, data.id, customerId, rawBarcode);

              const { error: fiscalError } = await supabase
                .from("item_fiscal_profiles" as any)
                .upsert(fiscalPayload, { onConflict: "item_id" });

              if (fiscalError) {
                throw fiscalError;
              }
            }

            const { error: lineError } = await supabase
              .from("item_xml_import_lines" as any)
              .insert(buildXmlImportLinePayload(selection, xmlImport.id, data.id, "create"));

            if (lineError) throw lineError;

            if (options.includeFiscal) {
              queryClient.invalidateQueries({ queryKey: ["item-fiscal-profile", data.id] });
            }
            results.push({ success: true, item: data, action: "created", stockError: await enterStock(data.id) });
          }
        }
      }

      return { xmlImport, results };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["items"] });
      queryClient.invalidateQueries({ queryKey: ["xml-imports"] });
      queryClient.invalidateQueries({ queryKey: ["product-stock"] });
      queryClient.invalidateQueries({ queryKey: ["stock-balances"] });
      queryClient.invalidateQueries({ queryKey: ["stock-movements"] });

      const successCount = data.results.filter((r) => r.success).length;
      const failCount = data.results.filter((r) => !r.success).length;
      const stockErrorCount = data.results.filter((r) => "stockError" in r && r.stockError).length;

      if (failCount === 0 && stockErrorCount === 0) {
        toast.success(i18n.t("erp:xmlImportDialog.toast.success", { count: successCount }));
      } else {
        toast.warning(
          i18n.t("erp:xmlImportDialog.toast.partial", {
            success: successCount,
            failed: failCount + stockErrorCount,
          }),
        );
      }
    },
    onError: (error: Error) => {
      toast.error(i18n.t("erp:xmlImportDialog.toast.importError", { message: error.message }));
    },
    // A failed run may already have saved the invoice and entered stock: refresh the
    // duplicate-invoice lookup so a retry is warned instead of doubling the stock.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["xml-imports"] });
    },
  });
}

export function useDeleteXmlImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (xmlImport: XmlImport) => {
      if (xmlImport.xml_file_path) {
        const { error: storageError } = await supabase.storage
          .from(XML_IMPORT_STORAGE_BUCKET)
          .remove([xmlImport.xml_file_path]);

        if (storageError) throw storageError;
      }

      const { error } = await supabase
        .from("item_xml_imports")
        .delete()
        .eq("id", xmlImport.id)
        .eq("customer_id", xmlImport.customer_id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["xml-imports"] });
      toast.success("XML excluído com sucesso.");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao excluir XML: ${error.message}`);
    },
  });
}

export async function downloadXmlImportFile(xmlImport: XmlImport) {
  const filename = buildXmlImportDownloadName({
    customerId: xmlImport.customer_id,
    supplierName: xmlImport.supplier_name,
    invoiceDate: xmlImport.invoice_issued_at || xmlImport.imported_at,
    invoiceKey: xmlImport.invoice_key,
  });

  if (xmlImport.xml_file_path) {
    const { data, error } = await supabase.storage
      .from(XML_IMPORT_STORAGE_BUCKET)
      .download(xmlImport.xml_file_path);

    if (error) throw error;
    triggerXmlDownload(data, filename);
    return;
  }

  triggerXmlDownload(new Blob([xmlImport.xml_content], { type: "application/xml" }), filename);
}

const triggerXmlDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

/**
 * Hook to list XML imports
 */
export function useXmlImports(filters?: { customerId?: string }) {
  return useQuery<XmlImport[]>({
    queryKey: ["xml-imports", filters],
    queryFn: async () => {
      let query = supabase
        .from("item_xml_imports")
        .select("*, item_xml_import_lines(count)")
        .order("imported_at", { ascending: false });

      if (filters?.customerId) {
        query = query.eq("customer_id", filters.customerId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return ((data || []) as Array<XmlImport & { item_xml_import_lines?: Array<{ count: number }> }>).map(
        ({ item_xml_import_lines, ...xmlImport }) => ({
          ...xmlImport,
          line_count: item_xml_import_lines?.[0]?.count ?? 0,
        })
      );
    },
  });
}

/**
 * Hook to get a single XML import
 */
export function useXmlImport(id: string) {
  return useQuery<XmlImport>({
    queryKey: ["xml-imports", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("item_xml_imports")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      return data as XmlImport;
    },
    enabled: !!id,
  });
}

export function useExistingXmlImport(invoiceKey?: string | null) {
  return useQuery<{ id: string } | null>({
    queryKey: ["xml-imports", "by-key", invoiceKey],
    enabled: !!invoiceKey,
    queryFn: async () => {
      const customerId = await getCurrentClinicCustomerId();
      const { data, error } = await supabase
        .from("item_xml_imports")
        .select("id")
        .eq("customer_id", customerId)
        .eq("invoice_key", invoiceKey as string)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}
